import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { resolveImageUrl } from "@/utils/imageUrl";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import {
  ShoppingCart,
  Trash2,
  CreditCard,
  Loader2,
  Shield,

  ArrowRight,
  Tag,
  CheckCircle,
  X
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { items, removeItem, updateQuantity, total, clearCart } = useCart();
  const { user, isAuthenticated } = useAuth();

  const [loading, setLoading] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoLoading, setPromoLoading] = useState(false);
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [customerInfo, setCustomerInfo] = useState({
    email: user?.email || "",
    phone: user?.phone || "",
    name: user?.name || "",
    createAccount: false,
    password: ""
  });

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-GH', {
      style: 'currency',
      currency: 'GHS'
    }).format(price);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "phone") {
      // Only allow digits and leading +
      const cleaned = value.replace(/[^\d+]/g, "");
      // Only allow + at the start
      const sanitized = cleaned.charAt(0) === "+" ? "+" + cleaned.slice(1).replace(/\+/g, "") : cleaned.replace(/\+/g, "");
      setCustomerInfo(prev => ({ ...prev, phone: sanitized }));
      return;
    }
    setCustomerInfo(prev => ({ ...prev, [name]: value }));
  };

  const isPhoneValid = (phone) => {
    const stripped = phone.replace(/\s/g, "");
    return /^(\+233[235]\d{8}|0[235]\d{8})$/.test(stripped);
  };

  const applyPromoCode = async () => {
    if (!promoCode.trim()) return;
    
    setPromoLoading(true);
    try {
      const response = await axios.post(`${API}/promo/validate`, {
        code: promoCode,
        subtotal: total,
        product_ids: items.map(item => item.product_id),
        customer_email: customerInfo.email || null
      });
      
      if (response.data.valid) {
        setAppliedPromo(response.data);
        toast.success(`Promo code applied! You save ${formatPrice(response.data.discount_amount)}`);
      } else {
        toast.error(response.data.error);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to apply promo code");
    } finally {
      setPromoLoading(false);
    }
  };

  const removePromoCode = () => {
    setAppliedPromo(null);
    setPromoCode("");
  };

  const finalTotal = appliedPromo ? total - appliedPromo.discount_amount : total;

  const handleCheckout = async (e) => {
    e.preventDefault();
    
    if (items.length === 0) {
      toast.error("Your cart is empty");
      return;
    }

    if (!customerInfo.email || !customerInfo.phone || !customerInfo.name) {
      toast.error("Please fill in all required fields");
      return;
    }

    const phoneRegex = /^(\+233[235]\d{8}|0[235]\d{8})$/;
    if (!phoneRegex.test(customerInfo.phone.replace(/\s/g, ""))) {
      toast.error("Enter a valid Ghana number: 05X XXX XXXX or +233 5X XXX XXXX");
      return;
    }

    if (customerInfo.createAccount && customerInfo.password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }

    setLoading(true);

    try {
      const checkoutResponse = await axios.post(`${API}/checkout`, {
        items: items.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity
        })),
        customer_email: customerInfo.email,
        customer_phone: customerInfo.phone.replace(/\s/g, ""),
        customer_name: customerInfo.name,
        create_account: customerInfo.createAccount && !isAuthenticated,
        password: customerInfo.createAccount ? customerInfo.password : null,
        promo_code: appliedPromo?.code || null
      });

      const { transaction_ref } = checkoutResponse.data;

      const paymentResponse = await axios.post(`${API}/payment/initiate`, {
        transaction_ref
      });

      const { mock, checkout_url } = paymentResponse.data;

      if (mock) {
        toast.info("Using mock payment for development");
        sessionStorage.setItem("pending_transaction", transaction_ref);
        window.location.href = `/payment/mock?ref=${transaction_ref}`;
        return;
      }

      // Redirect to Hubtel checkout page
      sessionStorage.setItem("pending_transaction", transaction_ref);
      window.location.href = checkout_url;

    } catch (error) {
      console.error("Checkout error:", error);
      toast.error(error.response?.data?.detail || "Checkout failed. Please try again.");
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <ShoppingCart className="w-20 h-20 text-muted-foreground mx-auto mb-6" />
            <h1 className="font-heading text-3xl font-bold mb-4">Your Cart is Empty</h1>
            <p className="text-muted-foreground mb-8">
              Browse our mock examinations and add items to your cart.
            </p>
            <Button asChild size="lg" className="bg-primary">
              <Link to="/mock-exams">
                Browse Mock Exams
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <Breadcrumb className="mb-6">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/">Home</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Checkout</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <h1 className="font-heading text-3xl md:text-4xl font-bold mb-8">Checkout</h1>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Cart Items */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ShoppingCart className="w-5 h-5" />
                  Order Items ({items.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {items.map(item => (
                  <div key={item.product_id} className="flex gap-4 py-4 border-b last:border-0">
                    {/* Thumbnail */}
                    <div className="w-20 h-28 bg-secondary rounded-lg overflow-hidden shrink-0">
                      {item.cover_image_url ? (
                        <img src={resolveImageUrl(item.cover_image_url)} alt={item.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <img src="/logo.png" alt="GMP" className="w-8 h-8 object-contain opacity-40 grayscale" />
                        </div>
                      )}
                    </div>

                    {/* Details */}
                    <div className="flex-1 min-w-0">
                      <Link to={`/mock-exams/${item.slug}`} className="font-medium hover:text-primary line-clamp-2">
                        {item.title}
                      </Link>
                      <p className="text-primary font-bold mt-1">{formatPrice(item.price)}</p>
                      
                      {/* Remove Button */}
                      <div className="flex items-center mt-3">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="w-8 h-8 text-destructive hover:text-destructive ml-auto"
                          onClick={() => removeItem(item.product_id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    {/* Subtotal */}
                    <div className="text-right">
                      <p className="font-bold">{formatPrice(item.price * item.quantity)}</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Customer Information */}
            <Card>
              <CardHeader>
                <CardTitle>Customer Information</CardTitle>
              </CardHeader>
              <CardContent>
                <form className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={customerInfo.name}
                        onChange={handleInputChange}
                        placeholder="Enter your full name"
                        required
                        data-testid="checkout-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={customerInfo.email}
                        onChange={handleInputChange}
                        placeholder="your@email.com"
                        required
                        data-testid="checkout-email"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number (Mobile Money) *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      inputMode="tel"
                      maxLength={13}
                      value={customerInfo.phone}
                      onChange={handleInputChange}
                      placeholder="0501234567 or +233501234567"
                      required
                      data-testid="checkout-phone"
                      className={customerInfo.phone && !isPhoneValid(customerInfo.phone) ? "border-red-400 focus-visible:ring-red-400" : ""}
                    />
                    {customerInfo.phone && !isPhoneValid(customerInfo.phone) ? (
                      <p className="text-xs text-red-500">Enter a valid Ghana number: 05X XXX XXXX or +233 5X XXX XXXX</p>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        Enter your MTN, Vodafone, or AirtelTigo number
                      </p>
                    )}
                  </div>

                  {!isAuthenticated && (
                    <>
                      <Separator />
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="createAccount"
                          checked={customerInfo.createAccount}
                          onCheckedChange={(checked) => setCustomerInfo(prev => ({ ...prev, createAccount: checked }))}
                        />
                        <label htmlFor="createAccount" className="text-sm cursor-pointer">
                          Create an account for faster checkout and order history
                        </label>
                      </div>

                      {customerInfo.createAccount && (
                        <div className="space-y-2">
                          <Label htmlFor="password">Password *</Label>
                          <Input
                            id="password"
                            name="password"
                            type="password"
                            value={customerInfo.password}
                            onChange={handleInputChange}
                            placeholder="Create a password (min 8 characters)"
                            minLength={8}
                            data-testid="checkout-password"
                          />
                        </div>
                      )}
                    </>
                  )}
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Order Summary Sidebar */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24">
              <CardHeader>
                <CardTitle>Order Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Promo Code Input */}
                <div className="space-y-2">
                  <Label className="text-sm">Promo Code</Label>
                  {appliedPromo ? (
                    <div className="flex items-center justify-between p-3 bg-success/10 border border-success/30 rounded-lg">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-success" />
                        <span className="font-medium text-success">{appliedPromo.code}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="w-6 h-6"
                        onClick={removePromoCode}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Input
                        placeholder="Enter code"
                        value={promoCode}
                        onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                        data-testid="promo-code-input"
                      />
                      <Button
                        variant="outline"
                        onClick={applyPromoCode}
                        disabled={promoLoading || !promoCode.trim()}
                      >
                        {promoLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Tag className="w-4 h-4" />}
                      </Button>
                    </div>
                  )}
                </div>

                <Separator />

                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span>{formatPrice(total)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Discount</span>
                  <span className={appliedPromo ? "text-success font-medium" : ""}>
                    {appliedPromo ? `-${formatPrice(appliedPromo.discount_amount)}` : "GHS 0.00"}
                  </span>
                </div>
                <Separator />
                <div className="flex justify-between font-bold text-lg">
                  <span>Total</span>
                  <span className="text-primary" data-testid="checkout-total">{formatPrice(finalTotal)}</span>
                </div>

                <Button
                  className="w-full h-14 text-lg bg-primary hover:bg-primary/90"
                  onClick={handleCheckout}
                  disabled={loading}
                  data-testid="pay-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5 mr-2" />
                      Pay with Mobile Money
                    </>
                  )}
                </Button>

                {/* Security Notice */}
                <div className="flex items-start gap-2 p-3 bg-secondary/50 rounded-lg text-xs text-muted-foreground">
                  <Shield className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                  <p>Your PDF downloads will be watermarked with your purchase details to prevent unauthorized sharing.</p>
                </div>

                {/* Payment Methods */}
                <div className="text-center">
                  <p className="text-xs text-muted-foreground mb-2">Accepted Payment Methods</p>
                  <div className="flex justify-center gap-2">
                    <span className="px-2 py-1 bg-secondary rounded text-xs font-medium">MTN MoMo</span>
                    <span className="px-2 py-1 bg-secondary rounded text-xs font-medium">Vodafone Cash</span>
                    <span className="px-2 py-1 bg-secondary rounded text-xs font-medium">AirtelTigo</span>
                  </div>
                </div>

                {/* Trust Badge */}
                <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground pt-4 border-t">
                  <Shield className="w-4 h-4 text-primary" />
                  <span>Secure payment via Hubtel</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
